/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import type { PropsWithChildren } from 'react';
import React from 'react';

import { aggregatedClasses, convertPropToCssClassName } from '@elliemae/ds-classnames';
import { DSPopper, interactions, PopperInteractions, PopperPositions, positions } from '@elliemae/ds-legacy-popper';
import { describe, PropTypes, useDeprecateComponent } from '@elliemae/ds-props-helpers';
import { DEFAULT_DELAY_CLOSE, DEFAULT_DELAY_OPEN } from '@elliemae/ds-shared';
import { TooltipType } from './TooltipType.js';

const getComponentFromProps = <T,>(
  Component: React.ReactElement<PropsWithChildren<unknown>> | React.ComponentType<unknown>,
  props: T,
  children: React.ReactNode[] = [],
) => {
  if (!Component) return null;
  const componentChildren =
    typeof Component !== 'function' && Component.props
      ? Array.isArray(Component.props.children)
        ? Component.props.children
        : [Component.props.children]
      : null;
  return typeof Component === 'function' ? (
    // @ts-ignore
    <Component {...props}>{children}</Component>
  ) : (
    React.cloneElement(
      Component,
      {
        ...Component.props,
        ...props,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      componentChildren ? [...componentChildren, ...children] : [...children],
    )
  );
};

const blockName = 'tooltip';

const TooltipContainer: React.ForwardRefExoticComponent<
  React.RefAttributes<unknown> & {
    children: React.ReactNode;
    className?: string;
    innerRef: React.MutableRefObject<HTMLDivElement>;
  }
> = aggregatedClasses('div')(blockName, null, {
  propsToRemoveFromFinalElement: ['isHovered', 'isDragged'],
});

const TooltipText: React.ForwardRefExoticComponent<
  React.RefAttributes<unknown> & {
    children: React.ReactNode;
  }
> = aggregatedClasses('span')(blockName, 'text', {
  propsToRemoveFromFinalElement: ['isHovered', 'isDragged'],
});

type DSTooltipProps = {
  containerProps: object;
  innerRef: React.MutableRefObject<HTMLDivElement>;
  placement: string;
  title: string;
  delayClose: number;
  delayOpen: number;
  interactionType: string;
  triggerComponent: React.ReactElement;
  className: string;
  isOpen?: boolean;
  onOpen: () => null;
  tooltipType: string;
  showArrow: boolean;
  zIndex: number;
};

interface Props {
  componentName: string;
  version: string;
}

const DSTooltip = ({
  containerProps = {},
  innerRef,
  placement = PopperPositions.TOP,
  title = '',
  delayClose = DEFAULT_DELAY_CLOSE,
  delayOpen = DEFAULT_DELAY_OPEN,
  interactionType = PopperInteractions.HOVER,
  triggerComponent,
  className = '',
  isOpen = undefined,
  onOpen = () => null,
  tooltipType = TooltipType.TOOLTIP,
  showArrow = true,
  zIndex,
  ...otherTooltipProps
}: DSTooltipProps) => {
  useDeprecateComponent({ componentName: 'ds-tooltip', version: 'TBD Date: 2023 Q3' });

  const { cssClassName: tooltipBlockName } = convertPropToCssClassName(blockName);

  let Content: React.ReactNode = title;
  if (typeof title === 'string') {
    Content = <TooltipText>{title}</TooltipText>;
  }
  return (
    <DSPopper
      blockName={tooltipBlockName}
      contentComponent={
        <TooltipContainer
          {...containerProps}
          {...otherTooltipProps}
          className={`tooltip-container--${tooltipType} ${className}`}
          innerRef={innerRef}
        >
          {Content}
        </TooltipContainer>
      }
      delayClose={delayClose}
      delayOpen={delayOpen}
      interactionType={interactionType}
      isOpen={isOpen}
      onOpen={onOpen}
      placement={placement}
      showArrow={tooltipType === TooltipType.TOOLTIP ? showArrow : false}
      triggerComponent={getComponentFromProps(triggerComponent, {})}
      zIndex={zIndex}
      wrapExtraComponent={undefined}
      children={undefined}
      renderReference={undefined}
      referenceNode={undefined}
    />
  );
};

const tooltipProps = {
  containerProps: PropTypes.object.description('Set of Properties attached to the main container'),
  placement: PropTypes.oneOf(positions).description('Tooltip position').defaultValue(PopperPositions.TOP),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).description('Tooltip content').isRequired,
  delayClose: PropTypes.string.description('Delay to close the tooltip').defaultValue(DEFAULT_DELAY_CLOSE),
  delayOpen: PropTypes.string.description('Delay to open the tooltip ').defaultValue(DEFAULT_DELAY_OPEN),
  interactionType: PropTypes.oneOf(interactions)
    .description('A type indicating how to open/close the tooltip')
    .defaultValue(PopperInteractions.HOVER),
  triggerComponent: PropTypes.element.description(
    `The component that will open/close the tooltip. 
    Cannot be used with disabled elements. 
    https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled`,
  ).isRequired,
  isOpen: PropTypes.bool.description('Whether the tooltip is visible or not').defaultValue(undefined),
  onOpen: PropTypes.func.description('Handler when the tooltip opens'),
  springAnimationComponent: PropTypes.element.description('Spring animation component'),
  showArrow: PropTypes.bool.description('Whether to show an arrow or not').defaultValue(true),
  className: PropTypes.string.description('css class'),
  tooltipType: PropTypes.oneOf(['tooltip', 'toolbar']).description('Type of tooltip').defaultValue(TooltipType.TOOLTIP),
  zIndex: PropTypes.number.description('z-index value assigned to the tooltip').defaultValue(undefined),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).description('Ref to the tooltip container element'),
} as React.WeakValidationMap<unknown>;

DSTooltip.displayName = 'DSTooltip';
const TooltipWithSchema = describe(DSTooltip);
TooltipWithSchema.propTypes = tooltipProps;

export { DSTooltip, TooltipContainer, TooltipText, TooltipWithSchema };
export default DSTooltip;
